<template>
    <div v-if="loaded" class="mt-3 space-y-5">
        <div class="card p-6">
            <div v-if="edit === false" class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div class="flex items-center gap-4">
                    <user-icon :user="locataire" size="lg"/>
                    <div>
                        <div class="flex flex-wrap items-center gap-2">
                            <h1 class="text-xl font-bold text-slate-800 dark:text-gray-100">
                                {{formatGender(locataire.gender)}} {{ locataire.prenom }} {{ locataire.nom }}
                            </h1>
                            <span
                                class="text-xs font-medium px-2 py-0.5 rounded-full"
                                :class="locataire.enable ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400'"
                            >
                                {{ locataire.enable ? 'Compte actif' : 'Compte inactif' }}
                            </span>
                        </div>
                        <div class="mt-1.5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-gray-400">
                            <a class="flex items-center gap-1.5 hover:text-indigo-600 dark:hover:text-indigo-300" :href="`mailto:${locataire.email}`">
                                <font-awesome-icon class="text-xs" :icon="['fa', 'envelope']" />
                                {{ locataire.email }}
                            </a>
                            <a v-if="locataire.contact_email" class="flex items-center gap-1.5 hover:text-indigo-600 dark:hover:text-indigo-300" :href="`mailto:${locataire.contact_email}`">
                                <font-awesome-icon class="text-xs" :icon="['fa', 'envelope']" />
                                {{ locataire.contact_email }}
                                <span class="text-xs text-slate-400 dark:text-gray-500">(contact)</span>
                            </a>
                            <span v-if="locataire.telephone" class="flex items-center gap-1.5">
                                <font-awesome-icon class="text-xs" :icon="['fa', 'phone']" />
                                {{ locataire.telephone }}
                            </span>
                        </div>
                    </div>
                </div>

                <span class="shrink-0 border rounded-lg py-2 px-3 text-gray-100 cursor-pointer bg-indigo-600 hover:bg-indigo-700 self-start sm:self-center" @click="editInfo()">
                    <font-awesome-icon class="text-sm" :icon="['fa', 'edit']" />
                    <span class="ml-1.5 text-sm">Modifier</span>
                </span>
            </div>

            <div v-else>
                <div class="flex flex-wrap gap-3">
                    <div class="w-24">
                        <label class="form-label" for="genre">
                            Genre*
                        </label>
                        <div class="relative">
                            <Multiselect
                                v-model='gender'
                                id='genre'
                                :options="genderOptions"
                                label="libelle"
                                track-by="libelle"
                                v-bind="$multiselectEmptyBind"
                            />
                        </div>
                    </div>
                    <AppInput v-model='locataireData.prenom' title='Prénom' id='prenom' :validations="['required']" @hasError="handleFormError" class="w-40"/>
                    <AppInput v-model='locataireData.nom' title='Nom' id='nom' :validations="['required']" @hasError="handleFormError" class="w-40"/>
                    <AppInput v-model='locataireData.contact_email' title='Email de contact' id='contactEmail' @hasError="handleFormError" class="w-64"/>
                    <AppInput v-model='locataireData.telephone' title='Téléphone' id='telephone' @hasError="handleFormError" class="w-44"/>
                </div>

                <div class="flex justify-end gap-2 mt-4">
                    <span class="border rounded-lg py-2 px-3 text-gray-100 cursor-pointer bg-slate-500 hover:bg-slate-600" @click="edit = false">
                        <font-awesome-icon class="text-sm" :icon="['fa', 'undo']" />
                        <span class="ml-1.5 text-sm">Annuler</span>
                    </span>
                    <span
                        class="border rounded-lg py-2 px-3 text-gray-100"
                        :class="!checkFieldValid ? 'cursor-pointer bg-indigo-600 hover:bg-indigo-700' : 'cursor-not-allowed bg-indigo-300'"
                        @click="editInfo()"
                    >
                        <font-awesome-icon v-if="edit === true" class="text-sm" :icon="['fa', 'check']" />
                        <font-awesome-icon v-else class="text-sm animate-spin" :icon="['fas', 'spinner']"  />
                        <span class="ml-1.5 text-sm">Enregistrer</span>
                    </span>
                </div>
            </div>
        </div>

        <div class="card p-6">
            <h3 class="text-lg font-bold text-slate-800 dark:text-gray-100 mb-4">Logements</h3>

            <p v-if="locataire.habitants.length === 0" class="text-sm text-slate-500 dark:text-gray-400">
                Ce locataire n'est rattaché à aucun logement pour le moment.
            </p>

            <ul v-else class="space-y-3">
                <li
                    class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 rounded-lg border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/60"
                    v-for="(item, index) in locataire.habitants" :key="index"
                >
                    <div>
                        <div class="flex items-center gap-2">
                            <router-link class="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300" :to="`/bien/${item.bien.uuid}/details`">
                                {{ item.bien.libelle }}
                            </router-link>
                            <span
                                class="text-xs font-medium px-2 py-0.5 rounded-full"
                                :class="item.archived ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400' : 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400'"
                            >
                                {{ item.archived ? 'Archivé' : 'En cours' }}
                            </span>
                        </div>
                        <div class="flex items-center gap-1.5 text-sm text-slate-500 dark:text-gray-400 mt-0.5">
                            <span>{{ formatDate(item.date_entree) }}</span>
                            <template v-if="item.date_sortie">
                                <font-awesome-icon class="text-xs" :icon="['fas', 'arrow-right']" />
                                <span>{{ formatDate(item.date_sortie) }}</span>
                            </template>
                        </div>
                    </div>
                    <div class="text-sm text-slate-500 dark:text-gray-400 sm:text-right">
                        Dépôt de garantie
                        <span class="block sm:inline font-medium text-slate-700 dark:text-gray-200">{{ item.security_deposit }}€</span>
                    </div>
                </li>
            </ul>
        </div>
    </div>
    <SpinnerLoader v-else class="mt-5"></SpinnerLoader>
</template>

<script>
import { inject } from 'vue'
import AppInput from './../../components/form/AppInput';
import SpinnerLoader from './../../components/loader/SpinnerLoader';
import Multiselect from 'vue-multiselect'
import { mapGetters } from "vuex";

export default {
    components: {
        SpinnerLoader,
        AppInput,
        Multiselect
    },
    data() {
        return {
            locataire: {},
            loaded: false,
            formInvalid: [],
            edit: false,
            locataireData: {},
            gender: {id: 'Male', libelle: 'M.'}
        }
    },
    computed: {
        checkFieldValid() {
            return this.formInvalid.length !== 0;
        },
        ...mapGetters({
            genderOptions: 'getGenderOptions',
        }),
    },
    created() {
        this.loadLocataire()
    },
    methods: {
        async loadLocataire() {
            const api = inject('api');

            this.loaded = false
            
            this.locataire = await api.locataire.getLocataire(this.$route.params.locataireUuid);
            this.gender = this.genderOptions.find(g => g.id === this.locataire.gender)

            this.loaded = true
        },

        showRole(item) {
            if(item.admin) {
                return 
            }
        },

        handleFormError(event) {
            if (event.value && !this.formInvalid.includes(event.name)) {
                this.formInvalid.push(event.name);
            }

            if (!event.value && this.formInvalid.includes(event.name)) {
                this.formInvalid = this.formInvalid.filter(f => f !== event.name)
            }
        },

        async editInfo() {
            if (this.edit === false) {
                this.locataireData = {
                    contact_email: this.locataire.contact_email,
                    prenom: this.locataire.prenom,
                    nom: this.locataire.nom,
                    telephone: this.locataire.telephone,
                }
                this.edit = true

                return;
            }

            if (this.checkFieldValid) {
                return;
            }

            this.edit = undefined

            this.locataire = await this.$api.locataire.updateLocataire(this.locataire.uuid, {
                gender: this.gender.id,
                contact_email: this.locataireData.contact_email,
                nom: this.locataireData.nom,
                prenom: this.locataireData.prenom,
                telephone: this.locataireData.telephone,
            })
            .then((data) => {
                this.$toast({title: 'Succès', description: "Le locataire à bien été enregistré."}, this.$toastSuccess);
                
                return data
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue, veuillez réessayer plus tard."}, this.$toastError);
            })

            this.edit = false
        }
    },
};
</script>